import { ResultSet } from "@cubejs-client/core";
import { AppThunk } from "appThunk";
import { cubeLoad } from "modules/helpers/cube/cubeSlice";
import { logError } from "modules/helpers/logger/loggerSlice";

import { DateTime } from "luxon";
import { CustomDate, getDateDimensionNames } from "modules/customer/insights/cost/customDate";

export class MonthlyRevenue {
    public readonly group: string;
    public readonly date: DateTime;
    public readonly customDate: CustomDate;
    public readonly revenue: number;

    constructor(
        group: string,
        date: DateTime,
        customDate: CustomDate,
        revenue: number
    ) {
        this.group = group;
        this.date = date;
        this.customDate = customDate;
        this.revenue = revenue;
    }
}

export const loadMonthlyRevenue = (costReferenceDate: DateTime, useCustomCalendar: boolean): AppThunk<Promise<MonthlyRevenue[]>> => async (dispatch) => {
    try {
        if (!costReferenceDate) {
            return [];
        }

        const priorFiveYearsToReferenceDate = costReferenceDate.minus({ years: 5 }).plus({ days: 1 }).startOf("day");
        const dateDimensions = getDateDimensionNames(useCustomCalendar);

        const salesQuery = {
            measures: ["F_Sales.SumLineValue"],
            timeDimensions: [{
                dimension: "D_Date.Date",
                dateRange: [priorFiveYearsToReferenceDate, costReferenceDate.endOf('day')]
            }],
            dimensions: [
                "D_Store.Group",
                `D_Date.${dateDimensions.monthPeriodStartDate}`,
                `D_Date.${dateDimensions.monthPeriodNumber}`,
                "D_Date.QuarterNo",
                `D_Date.${dateDimensions.yearNumber}`
            ],
            order: [["D_Date.MonthStartDate", "asc"]]
        };

        const resultSet = await dispatch(cubeLoad(salesQuery)) as unknown as ResultSet;
        const salesRawData = resultSet.rawData();

        return salesRawData.map(salesRow => {
            return new MonthlyRevenue(
                salesRow["D_Store.Group"],
                DateTime.fromISO(salesRow[`D_Date.${dateDimensions.monthPeriodStartDate}`], { zone: "utc" }),
                new CustomDate(
                    DateTime.fromISO(salesRow[`D_Date.${dateDimensions.monthPeriodStartDate}`], { zone: "utc" }),
                    Number(salesRow[`D_Date.${dateDimensions.monthPeriodNumber}`]),
                    Number(salesRow["D_Date.QuarterNo"]),
                    Number(salesRow[`D_Date.${dateDimensions.yearNumber}`]),
                ),
                Number(salesRow["F_Sales.SumLineValue"])
            );
        });
    } catch (error) {
        dispatch(logError("Error loading MonthlyRevenue.", error));
        throw error;
    }
};
